import {Component, OnInit, OnDestroy} from '@angular/core';
import {Router, ActivatedRoute} from "@angular/router";
import { WebhookService } from 'src/services/webhook.service';
import { PaymentService } from 'src/services/payment.service';
import { RouteHelperService } from 'src/services/route-helper.service';

@Component({
  selector: 'app-cancel',
  templateUrl: './cancel.component.html',
  styleUrls: ['./cancel.component.scss']
})
export class CancelComponent implements OnInit, OnDestroy {
  countdown: number = 10;
  transactionDetails: any = {
    checkoutId: null,
    reference: 'N/A',
    date: new Date(),
    status: 'Cancelled',
    plateNumber: null,
    zoneName: null,
    amount: null,
    // Bulk payment support
    plateNumbers: [],
    zoneNames: [],
    sessionCount: 1,
    isBulkPayment: false
  };
  isUpdating: boolean = true;
  isVerifying: boolean = false;
  private countdownInterval: any;

  constructor(
    private router: Router, 
    private route: ActivatedRoute,
    private webhookService: WebhookService,
    private paymentService: PaymentService,
    public routeHelper: RouteHelperService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(async params => {
      
      // Extract parameters using multiple possible names
      const checkoutId = this.extractCheckoutId(params);
      const reference = params['reference'] || params['referenceNumber'] || params['ref'];
      const amount = params['amount'] || params['totalAmount'] || params['value'];

      this.transactionDetails = {
        checkoutId: checkoutId,
        reference: reference || 'N/A',
        amount: amount || null,
        date: new Date(),
        status: 'Cancelled',
        plateNumber: params['plateNumber'] || null,
        zoneName: params['zoneName'] || null
      };

      // Try to verify if payment was actually completed before cancellation
      if (checkoutId) {
        await this.verifyBeforeCancellation(checkoutId);
      } else {
        // Try payment recovery to get more details
        await this.attemptPaymentRecovery(params);
      }
    });
  }

  private extractCheckoutId(params: any): string | null {
    // Try multiple parameter names that Maya might use
    const possibleIds = [
      'checkoutId', 'id', 'checkout_id', 'transactionId', 
      'transaction_id', 'payment_id', 'session_id'
    ];
    
    for (const key of possibleIds) {
      if (params[key]) {
        return params[key];
      }
    }
    
    return null;
  }

  private async attemptPaymentRecovery(params: any): Promise<void> {
    this.isUpdating = true;
    
    try {
      // Try to find recent payment from current user's session
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user && user.uid) {
        
        // Check for recent online payments for this user
        const recentPayment = await this.findRecentUserPayment(user.uid);
        if (recentPayment) {
          
          this.transactionDetails.checkoutId = recentPayment.checkout_id;
          this.transactionDetails.reference = recentPayment.transaction_reference || recentPayment.id;
          this.transactionDetails.amount = recentPayment.amount;
          this.transactionDetails.plateNumber = recentPayment.plate_number;
          this.transactionDetails.zoneName = recentPayment.zone_name;
          
          // Try to verify this payment
          await this.verifyBeforeCancellation(recentPayment.checkout_id);
          return;
        }
      }
      
      // If no recovery possible, proceed with cancellation
      this.showCancelledState();
      
    } catch (error) {
      console.error('Payment recovery failed:', error);
      this.showCancelledState();
    }
  }

  private async findRecentUserPayment(userId: string): Promise<any> {
    try {
      // Check localStorage for any stored payment info
      const storedPayment = localStorage.getItem('lastPaymentAttempt');
      if (storedPayment) {
        const paymentData = JSON.parse(storedPayment);
        // Check if it's from the last 10 minutes
        const paymentTime = new Date(paymentData.timestamp);
        const now = new Date();
        const diffMinutes = (now.getTime() - paymentTime.getTime()) / (1000 * 60);
        
        if (diffMinutes <= 10) {
          
          // Handle both bulk and single payments
          if (paymentData.session_count > 1 || paymentData.parking_session_ids) {
            paymentData.isBulkPayment = true;
            paymentData.sessionCount = paymentData.session_count || (paymentData.parking_session_ids ? paymentData.parking_session_ids.length : 1);
            paymentData.plateNumbers = paymentData.plate_numbers || [];
            paymentData.zoneNames = paymentData.zone_names || [];
          }
          
          return paymentData;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error finding recent payment:', error);
      return null;
    }
  }

  async verifyBeforeCancellation(checkoutId: string) {
    try {
      this.isVerifying = true;
      this.isUpdating = true;
      
      // First, try to get payment details from online_payments collection
      const onlinePayment = await this.paymentService.getOnlinePaymentByCheckoutId(checkoutId);
      
      if (onlinePayment) {
        
        // Update transaction details
        this.transactionDetails.amount = onlinePayment.amount || this.transactionDetails.amount;
        this.transactionDetails.plateNumber = onlinePayment.plate_number;
        this.transactionDetails.zoneName = onlinePayment.zone_name;
        this.transactionDetails.reference = onlinePayment.transaction_reference || onlinePayment.id;
        
        // Check if actually marked as paid (redirect to success)
        if (onlinePayment.status === 'paid' || onlinePayment.payment_status === 'paid' || 
            onlinePayment.status === 'completed' || onlinePayment.payment_status === true) {
          this.router.navigate([this.routeHelper.getRoutes().paymentSuccess], {
            queryParams: {
              checkoutId: checkoutId,
              reference: this.transactionDetails.reference,
              amount: this.transactionDetails.amount
            }
          });
          return;
        }
      }
      
      // Try to verify via payment service
      const paymentResult = await this.paymentService.verifyPaymentStatus(checkoutId);
      
      if (paymentResult && paymentResult.success && 
          (paymentResult.status === 'paid' || paymentResult.payment?.status === 'paid')) {
        // Payment was actually successful, redirect to success page
        this.router.navigate([this.routeHelper.getRoutes().paymentSuccess], {
          queryParams: {
            checkoutId: checkoutId,
            reference: this.transactionDetails.reference,
            amount: paymentResult.payment?.amount || this.transactionDetails.amount
          }
        });
        return;
      }

      // Payment is indeed cancelled, update status
      await this.updateCancelledStatus(checkoutId);

    } catch (error) {
      console.error('Payment verification before cancellation error:', error);
      // Still try to update cancelled status
      await this.updateCancelledStatus(checkoutId);
    } finally {
      this.isVerifying = false;
    }
  }

  async updateCancelledStatus(checkoutId: string) {
    try {
      
      // Try to process cancellation webhook to update status
      await this.webhookService.processWebhook({
        id: checkoutId,
        checkout: { id: checkoutId },
        status: 'CANCELLED',
        processedAt: new Date()
      }).toPromise();
      
    } catch (error) {
      console.error('Error processing cancellation:', error);
      // Don't fail silently, but don't block the user either
    } finally {
      this.showCancelledState();
    }
  }

  private showCancelledState(): void {
    this.isUpdating = false;
    this.isVerifying = false;
    this.startCountdown();
  }

  private startCountdown() {
    // Clear any existing intervals
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown === 0) {
        clearInterval(this.countdownInterval);
        this.goToDashboard();
      }
    }, 1000);
  }

  goToDashboard() {
    // Clear any stored payment attempt data
    localStorage.removeItem('lastPaymentAttempt');
    
    // Clear countdown interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    this.router.navigate([this.routeHelper.getRoutes().dashboard]);
  }

  tryAgain() {
    // Clear any stored payment attempt data
    localStorage.removeItem('lastPaymentAttempt');
    
    // Clear countdown interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    this.router.navigate([this.routeHelper.getRoutes().dashboard], { queryParams: { showPayment: true } });
  }

  contactSupport() {
    // Include transaction details in support email
    const emailBody = `I need help with my cancelled parking payment. Please assist me.\n\nTransaction Details:\n` +
      `- Reference: ${this.transactionDetails?.reference}\n` +
      `- Checkout ID: ${this.transactionDetails?.checkoutId}\n` +
      `- Amount: ${this.transactionDetails?.amount || 'N/A'}\n` +
      `- Date: ${this.formatDate(this.transactionDetails?.date)}\n` +
      `- Status: Cancelled\n` +
      `- Plate Number: ${this.transactionDetails?.plateNumber || 'N/A'}\n` +
      `- Zone: ${this.transactionDetails?.zoneName || 'N/A'}`;
      
    window.open(`mailto:support@parkbot.ph?subject=Payment Help - ${this.transactionDetails?.reference}&body=${encodeURIComponent(emailBody)}`);
  }

  ngOnDestroy() {
    // Clean up interval on component destroy
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  formatDate(date: any): string {
    if (!date) return 'N/A';
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  }
}
